import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ThemeProvider } from 'next-themes';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useScrollbarVisibility } from '@/hooks/useScrollbarVisibility';
import { useBodyPointerEventsGuard } from '@/hooks/useBodyPointerEventsGuard';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useState, useEffect, lazy, Suspense } from 'react';
import { OfflineIndicator } from '@/components/ui/OfflineIndicator';
import { GlobalActivityBar } from '@/components/layout/GlobalActivityBar';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { PageLoadingFallback } from '@/components/PageLoadingFallback';
import { Footer } from '@/components/Footer';
import { BottomNav } from '@/components/BottomNav';

import { ChatBubble } from '@/components/chat/ChatBubble';
import { useSigninCounter } from '@/hooks/useSigninCounter';
import { useInventoryWarmup } from '@/hooks/useInventoryWarmup';
import { ChatPanel } from '@/components/chat/ChatPanel';
import { QuickMarkSoldDialog } from '@/components/inventory/QuickMarkSoldDialog';
import { KioskShell } from '@/components/KioskShell';
import { useEbayAutoSync } from '@/hooks/useEbayAutoSync';

// Heavy chrome modals — lazy so first paint ships a smaller chunk.
const SetPasswordPrompt = lazy(() => import('@/components/auth/SetPasswordPrompt').then(m => ({ default: m.SetPasswordPrompt })));
const EmailVerificationBanner = lazy(() => import('@/components/EmailVerificationBanner').then(m => ({ default: m.EmailVerificationBanner })));
const GeneralTermsModal = lazy(() => import('@/components/auth/GeneralTermsModal').then(m => ({ default: m.GeneralTermsModal })));

const AddItemModal = lazy(() => import('@/components/inventory/AddItemModal').then(m => ({ default: m.AddItemModal })));
const PendingSalesQueueDialog = lazy(() => import('@/components/inventory/PendingSalesQueueDialog').then(m => ({ default: m.PendingSalesQueueDialog })));

// Public/tease pages that the non-retail anonymous branch still renders inline.
import SignInHome from './pages/SignInHome';
import AuthCallback from './pages/AuthCallback';
const PreRegisterConfirm = lazy(() => import('./pages/PreRegisterConfirm'));
const PublicProfile = lazy(() => import('./pages/PublicProfile'));
const CardShowDetail = lazy(() => import('./pages/CardShowDetail'));
const CardShowsIndex = lazy(() => import('./pages/CardShowsIndex'));
const GameStoreDetail = lazy(() => import('./pages/GameStoreDetail'));
const ItemDetail = lazy(() => import('./pages/ItemDetail'));
const EarlyAccess = lazy(() => import('./pages/EarlyAccess'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const Disclaimer = lazy(() => import('./pages/Disclaimer'));
const Safety = lazy(() => import('./pages/Safety'));
const Contact = lazy(() => import('./pages/Contact'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const MenuPage = lazy(() => import('./pages/MenuPage'));

const TeaseTerms = lazy(() => import('./pages/public/TeaseTerms'));
const TeasePrivacy = lazy(() => import('./pages/public/TeasePrivacy'));
const TeaseSafety = lazy(() => import('./pages/public/TeaseSafety'));
const TeaseContact = lazy(() => import('./pages/public/TeaseContact'));
const TeaseDisclaimer = lazy(() => import('./pages/public/TeaseDisclaimer'));

import { RETAIL_MODE } from '@/lib/retailMode';
import { NonAdminGate } from '@/components/NonAdminGate';
import { HumanizePricesConsole } from '@/components/admin/HumanizePricesConsole';
import { RetailAnonRoutes } from '@/routes/RetailAnonRoutes';
import { AuthedRoutes } from '@/routes/AuthedRoutes';



const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			refetchOnReconnect: false,
			staleTime: 10 * 60 * 1000,
			// Persistence needs gcTime >= maxAge; keep unmounted queries in memory
			// long enough that the persisted snapshot can rehydrate them on relaunch.
			gcTime: 24 * 60 * 60 * 1000,
			retry: 3,
			retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000)
		}
	}
});

// Persist a small whitelist of query keys to localStorage so a cold PWA launch
// repaints yesterday's inventory in the same tick React mounts, then
// stale-while-revalidate refreshes in the background.
const PERSISTED_KEYS = new Set(['my-items', 'profile', 'inventory-stats']);
const queryPersister =
	typeof window !== 'undefined'
		? createSyncStoragePersister({
				storage: window.localStorage,
				key: 'cs.rq.cache.v1',
				throttleTime: 1000
		  })
		: undefined;

// Non-blocking rehydration: kick off in the background so the app mounts on the
// first tick even if the persisted snapshot is stale or corrupt. `buster` bump
// invalidates any snapshot from before this fix so already-stuck browsers
// self-heal on next load.
if (typeof window !== 'undefined' && queryPersister) {
	persistQueryClient({
		queryClient: queryClient as unknown as Parameters<typeof persistQueryClient>[0]['queryClient'],
		persister: queryPersister,
		maxAge: 24 * 60 * 60 * 1000,
		buster: 'v2',
		dehydrateOptions: {
			shouldDehydrateQuery: (query) => {
				const root = Array.isArray(query.queryKey) ? query.queryKey[0] : query.queryKey;
				return (
					typeof root === 'string' &&
					PERSISTED_KEYS.has(root) &&
					query.state.status === 'success'
				);
			}
		}
	});
}

function EarlyAccessRoutes() {
	const { user, loading } = useAuth();
	// Only run eBay auto-sync for signed-in users (owner in retail mode).
	// Hook internally no-ops without a session, but skip the wiring entirely
	// for anonymous retail visitors.
	useEbayAutoSync();
	void user;

	if (loading) return <PageLoadingFallback />;

	// Not signed in: retail-only surface (single-seller storefront).
	// Marketplace pages, sign-up flows, market discovery, /shows, /game-stores,
	// /messages, etc. all collapse to the storefront. Owner signs in at /owner.
	if (!user) {
		if (RETAIL_MODE) {
			return <RetailAnonRoutes />;
		}
		// Tease-anon (non-retail, non-auth) branch — kept inline for now.
		return (
			<Routes>
				{/* Shareable single-store kiosk — locked, no nav, public to anyone */}
				<Route path="/s/:username/*" element={<KioskShell />} />
				{/* Public read-only surfaces — QR codes & shared links must work without an account.
				    Account-gated actions (message seller, propose trade) handle their own auth prompt. */}
				<Route path="/u/:username" element={<PublicProfile />} />
				<Route path="/u/:username/g/:gameSlug" element={<PublicProfile />} />
				<Route path="/u/:username/menu/:id" element={<MenuPage />} />
				<Route path="/item/:id" element={<ItemDetail />} />
				<Route path="/shows" element={<CardShowsIndex />} />
				<Route path="/show/:slug" element={<CardShowDetail />} />
				<Route path="/store/:storeId" element={<GameStoreDetail />} />
				<Route path="/" element={<SignInHome />} />
				<Route path="/pre-register" element={<Navigate to="/" replace />} />
				<Route path="/pre-register/confirm" element={<PreRegisterConfirm />} />
				<Route path="/landing" element={<EarlyAccess />} />
				{/* Originals preserved under /landing/* for internal review */}
				<Route path="/landing/terms" element={<Terms />} />
				<Route path="/landing/privacy" element={<Privacy />} />
				<Route path="/landing/safety" element={<Safety />} />
				<Route path="/landing/disclaimer" element={<Disclaimer />} />
				<Route path="/landing/contact" element={<Contact />} />
				{/* Public-facing legal/info routes use the vague pre-register copy */}
				<Route path="/terms" element={<TeaseTerms />} />
				<Route path="/privacy" element={<TeasePrivacy />} />
				<Route path="/safety" element={<TeaseSafety />} />
				<Route path="/disclaimer" element={<TeaseDisclaimer />} />
				<Route path="/contact" element={<TeaseContact />} />
				<Route path="/auth" element={<Navigate to="/?auth=1" replace />} />
				<Route path="/preview" element={<Navigate to="/?auth=1" replace />} />
				<Route path="/auth/callback" element={<AuthCallback />} />
				<Route path="/reset-password" element={<ResetPassword />} />
				{/* Unauthed catch-all: send to landing AND signal SignInHome to auto-open the auth tray */}
				<Route path="*" element={<Navigate to="/?auth=1" replace />} />
			</Routes>
		);
	}

	// Authed: full app — but in retail mode, only admin (the owner) gets it.
	// NonAdminGate silently signs out any non-admin and bounces them to /.
	return (
		<NonAdminGate>
			<AuthedRoutes />
		</NonAdminGate>
	);
}


const App = () => {
	useScrollbarVisibility();
	useBodyPointerEventsGuard();
	const { hasPassword } = useAuth();
	const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
	const [hasShownPromptThisSession, setHasShownPromptThisSession] = useState(false);

	useEffect(() => {
		const {
			data: { subscription }
		} = supabase.auth.onAuthStateChange(async (event, session) => {
			const amr = (session as any)?.user?.amr as
				| Array<{ method: string; timestamp: number }>
				| undefined;
			const signedInWithOTP = amr?.some((entry) => entry.method === 'otp');

			if (event === 'SIGNED_IN' && session?.user && signedInWithOTP && !hasShownPromptThisSession) {
				const passwordExists = await hasPassword();
				if (!passwordExists) {
					setShowPasswordPrompt(true);
					setHasShownPromptThisSession(true);
				}
			}
		});
		return () => subscription.unsubscribe();
	}, [hasPassword, hasShownPromptThisSession]);

	return (
		<QueryClientProvider client={queryClient}>
			<ThemeProvider attribute="class" defaultTheme="dark" forcedTheme="dark" enableSystem={false}>
			<TooltipProvider>
				<Toaster />
				<OfflineIndicator />
				<GlobalActivityBar />
				<Suspense fallback={null}>
					<EmailVerificationBanner />
					{/* <GeneralTermsModal /> — temporarily disabled per product call; file preserved */}
					
					<SetPasswordPrompt open={showPasswordPrompt} onOpenChange={setShowPasswordPrompt} />
					<AddItemModal />
					<PendingSalesQueueDialog />
					<HumanizePricesConsole />
				</Suspense>
				{/* a11y: single <main> landmark lives in each page; this wrapper is a div. */}
				<div className="contents">
					<ErrorBoundary level="app">
						<Suspense fallback={<PageLoadingFallback />}>
							<EarlyAccessRoutes />
						</Suspense>
					</ErrorBoundary>
				</div>
				<GlobalChrome />
			</TooltipProvider>
			</ThemeProvider>

		</QueryClientProvider>
	);
};

/**
 * Renders BottomNav / chat widgets / contact launcher app-wide,
 * but hides them entirely on shareable kiosk routes (`/s/:username`)
 * so visitors have nowhere to go besides that one store.
 */
function GlobalChrome() {
	const location = useLocation();
	const { user } = useAuth();
	useSigninCounter();
	useInventoryWarmup();
	if (location.pathname.startsWith('/s/')) return null;
	// Retail mode: anonymous visitors get a clean retail surface — Footer only,
	// no BottomNav / chat widgets / sign-in.
	if (RETAIL_MODE && !user) {
		return (
			<>
				<Footer />
			</>
		);
	}
	const hideChatBubble = location.pathname === '/' || RETAIL_MODE;
	return (
		<>
			<BottomNav />
			<Footer />
			{!hideChatBubble && <ChatBubble />}
			{/* ChatPanel is isolated in its own boundary so a render bug in
			    chat (malformed message, null peer) can't crash the app shell. */}
			<ErrorBoundary level="component">
				<ChatPanel />
			</ErrorBoundary>
			<QuickMarkSoldDialog />
		</>

	);
}

export default App;
